"use client";

import { useEffect, useRef, useSyncExternalStore } from "react";
import { adsConfig, type AdPosition } from "@/lib/ads";
import { cn } from "@/lib/utils";
declare global {
  interface Window {
    adsbygoogle?: unknown[];
  }
}

// Fixed sizes so the page never jumps when an ad loads.
const SIZE: Record<AdPosition, {
  box: string;
  label: string;
}> = {
  left: {
    box: "h-[600px] w-[160px]",
    label: "160 × 600"
  },
  right: {
    box: "h-[600px] w-[160px]",
    label: "160 × 600"
  },
  inline: {
    box: "h-[100px] w-full max-w-[728px]",
    label: "responsive banner"
  }
};

// Rails show at 1280 px and wider (Tailwind `xl`); the inline banner below that.
const WIDE = "(min-width: 1280px)";
function subscribeWide(onChange: () => void) {
  const query = window.matchMedia?.(WIDE);
  query?.addEventListener("change", onChange);
  return () => query?.removeEventListener("change", onChange);
}

/** One Google AdSense slot, or a grey "Ad slot" placeholder when
 *  NEXT_PUBLIC_ADSENSE_CLIENT is empty (always on localhost).
 *  Never place inside a tool workspace or near Download/Save buttons. */
export function AdSlot({
  position,
  className
}: {
  position: AdPosition;
  className?: string;
}) {
  const {
    client,
    slots
  } = adsConfig;
  const insRef = useRef<HTMLModElement>(null);
  const size = SIZE[position];
  // null while the page is pre-rendered (screen width unknown).
  const wide = useSyncExternalStore(subscribeWide, () => window.matchMedia?.(WIDE).matches ?? null, () => null);
  // AdSense fills `<ins>` tags in page order, one per push(), and can't fill a
  // hidden one — so only the slots visible at this width get an `<ins>` at
  // all; otherwise a hidden rail would use up the banner's turn (and back).
  const visible = wide !== null && (position === "inline" ? !wide : wide);
  useEffect(() => {
    if (!client || !visible || !insRef.current) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {
      // Ad blockers or a slow network: leave the reserved space empty.
    }
  }, [client, visible]);
  return <aside aria-label="Advertisement" data-ad-position={position} className={cn(position === "inline" && "w-full", className)}>
      {client && !visible ?
    // Space kept free (no layout jump) while this slot isn't in use.
    <div className={cn("mx-auto", position === "inline" ? "min-h-[100px] w-full max-w-[728px]" : size.box)} /> : client ? <ins ref={insRef} className={cn("adsbygoogle mx-auto block",
    // An "auto" banner picks its own height: reserve 100 px, let it grow.
    position === "inline" ? "min-h-[100px] w-full max-w-[728px]" : size.box)} data-ad-client={client} data-ad-slot={slots[position]}
    // The banner is responsive, as in Google's code; the 160 × 600 rails
    // keep a fixed size (Google: fixed-size slots omit data-ad-format).
    {...position === "inline" && {
      "data-ad-format": "auto",
      "data-full-width-responsive": "true"
    }} /> : <div className={cn("mx-auto flex flex-col items-center justify-center gap-1.5 rounded-lg border-[1.5px] border-dashed border-line-strong bg-muted text-center text-[13px] text-muted-foreground", size.box)}>
          <span className="font-semibold tracking-[0.08em] uppercase">
            Ad slot
          </span>
          <span>{size.label}</span>
        </div>}
      <p className="mt-1.5 text-center text-xs text-muted-foreground">
        Advertisement
      </p>
    </aside>;
}